import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { RippleModule } from 'primeng/ripple';
import { AsyncPipe, TitleCasePipe } from '@angular/common';
import { ResponsiveService } from 'app/services/responsive.service';
import {
  ButtonComponent,
  IconComponent,
  Playlist,
  ToggleSwitchComponent,
} from '@kinomap-dev/ng-ui-lib';
import { ToasterService } from 'app/services/toaster.service';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { EditorConfig } from 'ckeditor5';
import { CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { CKEditorConfig } from 'app/configs/ckeditorConfig';

@Component({
  selector: 'app-edit-playlist',
  templateUrl: './edit-playlist.component.html',
  styleUrls: ['./edit-playlist.component.scss'],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    ButtonComponent,
    RippleModule,
    AsyncPipe,
    TitleCasePipe,
    TranslateModule,
    IconComponent,
    ToggleSwitchComponent,
    CKEditorModule,
  ],
})
export class EditPlaylistComponent implements OnInit, OnDestroy {
  private responsiveService: ResponsiveService = inject(ResponsiveService);
  private toastService: ToasterService = inject(ToasterService);
  private translate: TranslateService = inject(TranslateService);

  @Input() playlist!: Playlist;
  @Input() loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );

  @Output() onPlaylistUpdated: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCloseEdit: EventEmitter<void> = new EventEmitter<void>();

  subscriptions: Subscription[] = [];
  editForm: FormGroup = new FormGroup({});

  // Responsive variables
  xSmallScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xSmallScreen$;
  smallScreen$: BehaviorSubject<boolean> = this.responsiveService.smallScreen$;
  mediumScreen$: BehaviorSubject<boolean> =
    this.responsiveService.mediumScreen$;
  largeScreen$: BehaviorSubject<boolean> = this.responsiveService.largeScreen$;
  xLargeScreen$: BehaviorSubject<boolean> =
    this.responsiveService.xLargeScreen$;
  trashColor: string = '#B0B0B0';
  logoFile: File;
  logoSrc: string | ArrayBuffer;
  bannerFile: File;
  bannerSrc: string | ArrayBuffer;
  isPublic: boolean = false;
  editor: typeof ClassicEditor = ClassicEditor;
  config: EditorConfig = {};

  switchClick(): void {
    this.isPublic = !this.isPublic;
  }

  remove(type: 'logo' | 'banner'): void {
    if (type === 'logo') {
      this.logoFile = null;
      this.logoSrc = null;
      this.editForm.get('image').setValue(null);
    } else if (type === 'banner') {
      this.bannerFile = null;
      this.bannerSrc = null;
      this.editForm.get('banner').setValue(null);
    }
  }

  loadImage(event: any, fileType: 'logo' | 'banner'): void {
    const fileList: FileList = event.target.files;

    if (fileList.length > 0) {
      const selectedFile: File = fileList[0];
      const reader: FileReader = new FileReader();
      reader.readAsDataURL(selectedFile);

      if (
        selectedFile.type === 'image/jpeg' ||
        selectedFile.type === 'image/png'
      ) {
        if (fileType === 'logo') {
          this.logoFile = selectedFile;
          reader.onload = (event) => {
            this.logoSrc = event.target.result;
          };
        } else if (fileType === 'banner') {
          this.bannerFile = selectedFile;
          reader.onload = (event: ProgressEvent<FileReader>) => {
            this.bannerSrc = event.target.result;
          };
        }
      } else {
        console.error(
          "Le fichier sélectionné n'est pas au format JPEG ou PNG."
        );
      }
    }
  }

  onSubmit(): void {
    if (
      this.isPublic &&
      ((!this.logoSrc && !this.logoFile) ||
        (!this.bannerFile && !this.bannerSrc))
    ) {
      this.toastService.warning(
        this.translate.instant('common.errors.invalidForm'),
        this.translate.instant(
          'watch.playlist_modal.create_new_playlist_modal.tips.public'
        )
      );
      return;
    }

    let playlistData: any = {
      title: this.editForm.value.title,
      description: this.editForm.value.description || 'null',
      status: this.isPublic ? 'public' : 'private',
    };

    if (this.logoFile) {
      playlistData = { ...playlistData, logo: this.logoFile };
    }

    if (this.bannerFile)
      playlistData = { ...playlistData, banner: this.bannerFile };

    this.onPlaylistUpdated.emit(playlistData);
  }

  ngOnInit(): void {
    this.config = CKEditorConfig;
    this.isPublic = this.playlist.status === 'public';
    this.logoSrc = this.playlist.images['512x512'] || null;
    this.bannerSrc = this.playlist.images['1024x256'] || null;
    this.editForm.addControl(
      'title',
      new FormControl(this.playlist.name, [Validators.required])
    );
    this.editForm.addControl(
      'description',
      new FormControl(
        this.playlist.description && this.playlist.description !== 'null'
          ? this.playlist.description
          : ''
      )
    );
    this.editForm.addControl('status', new FormControl(this.playlist.status));
    this.editForm.addControl(
      'image',
      new FormControl(this.playlist.images['512x512'] || null)
    );
    this.editForm.addControl(
      'banner',
      new FormControl(this.playlist.images['1024x256'] || null)
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }
}
